import React, {useEffect, useMemo, useState} from 'react'
import {
    Box,
    Drawer,
    DrawerOverlay,
    Button,
    Text,
    DrawerContent,
    DrawerBody,
    DrawerHeader,
    DrawerFooter,
    Stack,
    useStyleConfig,
    useBreakpointValue,
    Flex,
    RadioGroup,
    Radio,
    Divider,
} from '@chakra-ui/react'
import {CloseIcon, TickCircleEmptyIcon, TickCircleFilledIcon} from '../../../components/icons'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import FreeItemPromoTile from './free-item-promo-tile'
import FreeItemPromoActionButton from './free-item-promo-action-button'
import {remCalc} from '../../../theme/_utils'
import {
    useShopperBasketsMutationHelper,
    useAccessToken,
    useCommerceApi,
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import LoadingSpinner from '../../../components/loading-spinner'

const FreeItemPromoDrawer = ({
    onClose,
    isOpen,
    setSelectedFreeItems,
    selectedPromotion = {},
    basketItems = [],
    handleRemoveItem,
}) => {
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const {refetch} = useCurrentBasket()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const styles = useStyleConfig('ProductFilterDrawer', {variant: 'freeItemPromo'})
    const bundleStyles = useStyleConfig('Bundle')
    const drawerPlacement = useBreakpointValue({base: 'bottom', md: 'right'})

    const [promoProducts, setPromoProducts] = useState([])
    const [thisPromoItems, setThisPromoItems] = useState([])
    const [isLoading, setIsLoading] = useState(false)

    const arrChunker = (arr, size = 24) => {
        const chunks = []
        for (let i = 0; i < arr.length; i += size) {
            chunks.push(arr.slice(i, i + size))
        }
        return chunks
    }

    const fetchProducts = async () => {
        try {
            setIsLoading(true)
            const ids = selectedPromotion?.bonusProducts?.map((p) => p?.productId) || []
            const chunkedIds = arrChunker(ids, 24)
            const token = await getTokenWhenReady()

            const responses = await Promise.all(
                chunkedIds.map((batch) =>
                    api.shopperProducts.getProducts({
                        parameters: {
                            ids: batch.join(','),
                        },
                        expand: ['availability', 'images'],
                        headers: {Authorization: `Bearer ${token}`},
                    }),
                ),
            )
            const products = responses.flatMap((res) => res?.data || [])
            const productsWithStock = products?.filter((product) => {
                return product?.inventory?.stockLevel > 0
            })
            setIsLoading(false)
            return productsWithStock
        } catch (e) {
            setIsLoading(false)
            console.error('Error getting products for promotion', e)
            return []
        }
    }

    const refreshBasket = async () => {
        try {
            await refetch()
        } catch (error) {
            console.error('Failed to refresh basket', error)
        }
    }

    useEffect(() => {
        if (!selectedPromotion?.id) return

        const init = async () => {
            await refreshBasket()

            const res = await fetchProducts()
            if (res?.length) {
                setPromoProducts(res)
            }
        }

        init()
    }, [selectedPromotion])

    // Sync thisPromoItems with actual basket state when drawer opens or basket changes
    useEffect(() => {
        if (!selectedPromotion?.id || !promoProducts.length) return

        const promoBasketItems = basketItems.filter(
            (item) => item?.bonusDiscountLineItemId === selectedPromotion.id,
        )

        const itemsByProduct = {}
        for (const basketItem of promoBasketItems) {
            itemsByProduct[basketItem.productId] = (itemsByProduct[basketItem.productId] || 0) + 1
        }

        const synced = Object.entries(itemsByProduct).map(([productId, qty]) => {
            const product = promoProducts.find((p) => p.id === productId)
            return product ? {...product, quantity: qty} : {id: productId, quantity: qty}
        })

        setThisPromoItems(synced)
    }, [selectedPromotion?.id, basketItems, promoProducts])

    const promotionId = selectedPromotion?.id || ''
    const allowedItems = selectedPromotion?.maxBonusItems || 0
    const isSingleSelect = allowedItems === 1

    const totalQuantity = thisPromoItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
    const progressPercentage = allowedItems ? (totalQuantity / allowedItems) * 100 : 0
    const progressStatus = useMemo(() => {
        if (totalQuantity === 0) return 'empty'
        if (totalQuantity >= allowedItems) return 'complete'
        return 'partial'
    }, [allowedItems, totalQuantity])

    const getProductQuantity = (productId) => {
        const item = thisPromoItems.find((i) => i.id === productId)
        return item?.quantity || 0
    }

    const selectedPromoProducts = promoProducts.filter(
        (product) => getProductQuantity(product.id) > 0,
    )
    const unselectedPromoProducts = promoProducts.filter(
        (product) => getProductQuantity(product.id) === 0,
    )

    const handleSelect = (product) => {
        if (isSingleSelect) {
            setThisPromoItems([{...product, quantity: 1}])
            return
        }
        handleQuantityChange(product, 1)
    }

    const handleQuantityChange = (product, delta) => {
        setThisPromoItems((prev) => {
            const existing = prev.find((item) => item.id === product.id)
            const currentQty = existing ? existing.quantity || 1 : 0
            const newQty = currentQty + delta

            if (newQty < 0) return prev

            const totalOther = prev.reduce((sum, item) => {
                if (item.id === product.id) return sum
                return sum + (item.quantity || 1)
            }, 0)

            if (totalOther + newQty > allowedItems) return prev

            if (newQty === 0) {
                const basketPromoItem = basketItems.find(
                    (item) =>
                        item.productId === product.id &&
                        item?.bonusDiscountLineItemId === promotionId,
                )
                if (basketPromoItem) {
                    handleRemoveItem(
                        basketPromoItem,
                        basketPromoItem?.c_variantIdentifier ||
                            basketPromoItem?.name ||
                            basketPromoItem?.productName,
                        100,
                        false,
                    )
                }
                return prev.filter((item) => item.id !== product.id)
            }

            if (existing) {
                return prev.map((item) =>
                    item.id === product.id ? {...item, quantity: newQty} : item,
                )
            }

            return [...prev, {...product, quantity: newQty}]
        })
    }

    const handleAddGiftToCart = async () => {
        const selectedItems = thisPromoItems || []
        if (!selectedItems.length) return

        const allProductItems = selectedItems.flatMap((item) => {
            const itemId = item.id || item.productId
            const desiredQty = item.quantity || 1

            const currentBasketQty = basketItems.filter(
                (basketItem) =>
                    basketItem.productId === itemId &&
                    basketItem?.bonusDiscountLineItemId === promotionId,
            ).length

            const additionalQty = desiredQty - currentBasketQty
            if (additionalQty <= 0) return []

            return Array.from({length: additionalQty}, () => ({
                productId: itemId,
                price: item.price,
                quantity: 1,
                bonusDiscountLineItemId: promotionId,
                bonusProductLineItem: item?.bonusProductLineItem,
                c_MaterialGroup: item?.c_MaterialGroup,
                c_subModelIdentifier: item?.c_subModelIdentifier,
            }))
        })

        try {
            if (allProductItems.length) {
                await addItemToNewOrExistingBasket(allProductItems)
            }

            setSelectedFreeItems((prev) => ({
                ...prev,
                [promotionId]: {
                    ...prev[promotionId],
                    items: thisPromoItems,
                },
            }))
        } catch (e) {
            console.error('Failed to add free items to basket', e)
        } finally {
            await refetch()
            onClose()
        }
    }

    return (
        <Drawer
            variant="filter"
            isOpen={isOpen}
            onClose={onClose}
            placement={drawerPlacement}
            closeOnEsc
            closeOnOverlayClick
        >
            <DrawerOverlay />
            <DrawerContent>
                {isSingleSelect && (
                    <DrawerHeader {...styles?.header}>
                        <Flex {...styles?.titleContainer}>
                            <Text {...styles?.headerText} w="100%">
                                <FormattedMessage
                                    id="cart.free_item_promo_drawer.header_title"
                                    defaultMessage="Select a free product!"
                                />
                            </Text>
                            <CloseIcon
                                data-testid="close-icon"
                                onClick={onClose}
                                {...styles?.closeButton}
                            />
                        </Flex>
                    </DrawerHeader>
                )}
                {!isSingleSelect && (
                    <DrawerHeader {...styles?.headerMulti}>
                        <Flex flexDirection={'column'} w="100%">
                            <Flex {...styles?.titleContainerMulti}>
                                <Text {...styles?.headerText} w="100%">
                                    <FormattedMessage
                                        id="cart.free_item_promo_drawer.header_title_plural"
                                        defaultMessage="Select {count} free products!"
                                        values={{count: allowedItems}}
                                    />
                                </Text>
                                <CloseIcon
                                    data-testid="close-icon"
                                    onClick={onClose}
                                    {...styles?.closeButton}
                                />
                            </Flex>
                            <Flex flexDirection="column" w="100%" gap={remCalc(16)}>
                                <Box
                                    {...bundleStyles.progressTrack}
                                    {...bundleStyles.progressTrackByState?.[progressStatus]}
                                    role="progressbar"
                                    aria-valuemin={0}
                                    aria-valuemax={allowedItems}
                                    aria-valuenow={totalQuantity}
                                >
                                    <Box
                                        {...bundleStyles.progressFill}
                                        {...bundleStyles.progressFillByState?.[progressStatus]}
                                        width={`${progressPercentage}%`}
                                    />

                                    <Flex {...bundleStyles.progressContent}>
                                        <Box {...bundleStyles.progressIcon}>
                                            {progressStatus === 'complete' ? (
                                                <TickCircleFilledIcon
                                                    width={remCalc(12)}
                                                    height={remCalc(12)}
                                                />
                                            ) : progressStatus === 'partial' ? (
                                                <TickCircleEmptyIcon
                                                    width={remCalc(12)}
                                                    height={remCalc(12)}
                                                    color="background.base.default"
                                                />
                                            ) : (
                                                <TickCircleEmptyIcon
                                                    width={remCalc(12)}
                                                    height={remCalc(12)}
                                                    color="foreground.neutral.default"
                                                />
                                            )}
                                        </Box>

                                        <Text
                                            {...bundleStyles.progressText}
                                            {...bundleStyles.progressTextByState?.[progressStatus]}
                                        >
                                            {totalQuantity < allowedItems ? (
                                                <FormattedMessage
                                                    id="cart.free_item_promo_drawer.remaining_count"
                                                    defaultMessage="{count} more {count, plural, one {item} other {items}} to go"
                                                    values={{count: allowedItems - totalQuantity}}
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id="cart.free_item_promo_drawer.all_selected"
                                                    defaultMessage="All {count} products selected!"
                                                    values={{count: allowedItems}}
                                                />
                                            )}
                                        </Text>
                                    </Flex>
                                </Box>
                            </Flex>
                        </Flex>
                    </DrawerHeader>
                )}

                <DrawerBody {...styles?.body}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : isSingleSelect && promoProducts?.length > 0 ? (
                        <RadioGroup
                            value={thisPromoItems[0]?.id || ''}
                            width="100%"
                            onChange={(val) => {
                                const p = promoProducts.find((pp) => pp.id === val)
                                if (p) handleSelect(p)
                            }}
                        >
                            <Stack gap={remCalc(16)} paddingTop={remCalc(16)}>
                                {promoProducts.map((product) => (
                                    <Flex
                                        key={`${promotionId}-${product.id}`}
                                        onClick={(e) => {
                                            if (e?.target?.closest('[data-stop-propagation]'))
                                                return
                                            handleSelect(product)
                                        }}
                                    >
                                        <FreeItemPromoTile
                                            product={product}
                                            actionButton={
                                                <Radio
                                                    value={product.id}
                                                    display="inline-flex"
                                                    height={{base: '44px', lg: '24px'}}
                                                    borderWidth={remCalc(0.6)}
                                                    isChecked={thisPromoItems.some(
                                                        (item) => item.id === product.id,
                                                    )}
                                                    data-stop-propagation
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            }
                                        />
                                    </Flex>
                                ))}
                            </Stack>
                        </RadioGroup>
                    ) : (
                        <Stack gap={{base: remCalc(16), lg: remCalc(24)}} width="100%">
                            {selectedPromoProducts.length > 0 && (
                                <Stack gap={{base: remCalc(16), lg: remCalc(24)}} width="100%">
                                    {selectedPromoProducts.map((product) => {
                                        const qty = getProductQuantity(product.id)
                                        return (
                                            <Flex key={`${promotionId}-${product.id}`}>
                                                <FreeItemPromoTile
                                                    product={product}
                                                    actionButton={
                                                        <FreeItemPromoActionButton
                                                            qty={qty}
                                                            allowedItems={allowedItems}
                                                            totalQuantity={totalQuantity}
                                                            productName={
                                                                product?.c_variantIdentifier ||
                                                                product?.name ||
                                                                product?.productName
                                                            }
                                                            onChange={(delta) => {
                                                                handleQuantityChange(product, delta)
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Flex>
                                        )
                                    })}
                                </Stack>
                            )}

                            {selectedPromoProducts.length > 0 &&
                                unselectedPromoProducts.length > 0 && (
                                    <Divider borderColor="border.neutral.subtle" />
                                )}

                            {unselectedPromoProducts.length > 0 && (
                                <Stack gap={{base: remCalc(16), lg: remCalc(24)}} width="100%">
                                    {unselectedPromoProducts.map((product) => (
                                        <Flex key={`${promotionId}-${product.id}`}>
                                            <FreeItemPromoTile
                                                product={product}
                                                actionButton={
                                                    <FreeItemPromoActionButton
                                                        qty={0}
                                                        allowedItems={allowedItems}
                                                        totalQuantity={totalQuantity}
                                                        productName={
                                                            product?.c_variantIdentifier ||
                                                            product?.name ||
                                                            product?.productName
                                                        }
                                                        onChange={(delta) => {
                                                            handleQuantityChange(product, delta)
                                                        }}
                                                    />
                                                }
                                            />
                                        </Flex>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </DrawerBody>

                <DrawerFooter {...styles?.drawerFooter}>
                    <Flex
                        justifyContent="space-between"
                        width="full"
                        alignItems="center"
                        gap={remCalc(8)}
                    >
                        <Button
                            width="full"
                            variant="ghost-not-rounded"
                            size="xl"
                            onClick={onClose}
                        >
                            <FormattedMessage
                                id="cart.free_item_promo_drawer.footer.cancel_button_text"
                                defaultMessage="Cancel"
                            />
                        </Button>

                        <Button
                            width="full"
                            variant="primary-not-rounded"
                            size="xl"
                            isDisabled={totalQuantity < allowedItems}
                            onClick={handleAddGiftToCart}
                        >
                            <FormattedMessage
                                id="cart.free_item_promo_drawer.footer.done_button_text"
                                defaultMessage="Done"
                            />
                        </Button>
                    </Flex>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    )
}

export default FreeItemPromoDrawer

FreeItemPromoDrawer.propTypes = {
    onClose: PropTypes.func,
    isOpen: PropTypes.bool,
    promoProducts: PropTypes.array,
    setSelectedFreeItems: PropTypes.func,
    selectedPromotion: PropTypes.object,
    basketItems: PropTypes.array,
    basketId: PropTypes.string,
    handleRemoveItem: PropTypes.func,
}
